import { useSyncExternalStore } from "react";

// Chrome, Edge and Samsung Internet (Android and desktop) fire
// `beforeinstallprompt` once the app qualifies for install, and only once per
// page load, often before Settings is ever opened. It is captured here at
// import time and held until the user asks to install. Safari never fires it,
// so iOS gets written steps instead.

let deferredPrompt = null;
let installed = false;
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Hold the browser's own mini-infobar back; the Settings button offers it.
    event.preventDefault();
    deferredPrompt = event;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    installed = true;
    notify();
  });
}

const isStandalone = () =>
  installed ||
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

/**
 * How this browser can install the app:
 * "installed" — already running as the app;
 * "prompt"    — the browser's install dialog can be opened with a button;
 * "ios"       — Safari's Share → Add to Home Screen;
 * "android"   — the browser menu (the prompt was dismissed earlier, or this
 *               browser, like Firefox, never offers one);
 * null        — nothing to offer (e.g. desktop Firefox).
 */
export function installMethod({ standalone, canPrompt, userAgent = "", maxTouchPoints = 0 }) {
  if (standalone) return "installed";
  if (canPrompt) return "prompt";
  // iPadOS reports itself as a Mac; the touch screen gives it away.
  if (/iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)) {
    return "ios";
  }
  if (/Android/.test(userAgent)) return "android";
  return null;
}

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const snapshot = () =>
  installMethod({
    standalone: isStandalone(),
    canPrompt: Boolean(deferredPrompt),
    userAgent: window.navigator.userAgent,
    maxTouchPoints: window.navigator.maxTouchPoints,
  });

export function useInstallMethod() {
  return useSyncExternalStore(subscribe, snapshot, () => null);
}

/** Opens the browser's install dialog. Resolves true if the user accepted. */
export async function promptInstall() {
  const event = deferredPrompt;
  if (!event) return false;
  // A prompt can only be shown once; the browser fires a fresh event if the
  // user declines and the app still qualifies later.
  deferredPrompt = null;
  notify();
  event.prompt();
  const { outcome } = await event.userChoice;
  return outcome === "accepted";
}
